// src/hooks/useFcmSubscription.js
// Drives the SubscribePushButton state machine. Re-usable for any future
// "subscribe to notifications" surface (truck departure, chat, etc).

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "src/context/AuthContext";
import { getMessagingInstance, requestPermission } from "src/firebase/firebase";
import { registerDevice, unregisterDevice } from "src/utils/notifications/notificationApi";
import { captureError } from "src/utils/errorReporting";
import { useT } from "src/i18n";

function detectIosOutsidePwa() {
    if (typeof navigator === "undefined" || typeof window === "undefined") return false;
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (!isIos) return false;
    // navigator.standalone is the iOS-specific marker for "launched from Home Screen"
    const standalone = window.navigator.standalone === true
        || window.matchMedia?.("(display-mode: standalone)").matches;
    return !standalone;
}

/**
 * Subscribe-to-push state machine. The exported `subscribe()` MUST only be
 * called from a user-gesture handler — browsers reject `Notification.requestPermission()`
 * triggered outside a click/tap.
 *
 * @returns {{
 *   status: "checking" | "unsupported" | "ios-needs-pwa" | "default" | "registering" | "granted" | "denied" | "error",
 *   lastError: string | null,
 *   subscribe: () => Promise<boolean>,
 *   unsubscribe: () => Promise<void>,
 * }}
 *
 * Status meanings:
 *   "checking"      — initial mount, before FCM-support probe resolves
 *   "unsupported"   — no Notification API / no FCM in this browser
 *   "ios-needs-pwa" — iOS Safari outside an installed PWA (push needs Add-to-Home-Screen first)
 *   "default"       — permission not yet requested; user can click subscribe
 *   "denied"        — user said no; cannot re-prompt, must change in browser settings
 *   "registering"   — subscribe() in flight
 *   "granted"       — token fetched + registered with puminet5api backend
 *   "error"         — last attempt failed; `lastError` carries the reason
 */
export function useFcmSubscription() {
    const { token } = useAuth();
    const t = useT();
    const [status, setStatus] = useState("checking");
    const [lastError, setLastError] = useState(null);
    const fcmTokenRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        async function detect() {
            if (typeof window === "undefined" || !("Notification" in window)) {
                if (!cancelled) setStatus("unsupported");
                return;
            }
            if (detectIosOutsidePwa()) {
                if (!cancelled) setStatus("ios-needs-pwa");
                return;
            }
            // getMessagingInstance returns null when the browser can't do FCM —
            // same signal as isSupported() but without a duplicate import.
            const msg = await getMessagingInstance();
            if (cancelled) return;
            if (!msg) {
                setStatus("unsupported");
                return;
            }
            const p = Notification.permission;
            setStatus(p === "granted" ? "granted" : p === "denied" ? "denied" : "default");
        }
        detect();
        return () => { cancelled = true; };
    }, []);

    const subscribe = useCallback(async () => {
        if (!token) {
            setLastError(t("common.fcm.signInRequired"));
            return false;
        }
        setStatus("registering");
        setLastError(null);
        const { success, token: fcmToken, error } = await requestPermission();
        if (!success || !fcmToken) {
            if (error === "PERMISSION_DENIED") {
                setStatus("denied");
            } else {
                setStatus("error");
                setLastError(error || t("common.fcm.subscribeFailed"));
            }
            return false;
        }
        try {
            await registerDevice({ fcmToken, token });
            fcmTokenRef.current = fcmToken;
            setStatus("granted");
            return true;
        } catch (err) {
            captureError(err, {
                tags: { feature: "fcm", operation: "registerDevice" },
            });
            setStatus("error");
            setLastError(err?.message || t("common.fcm.registerDeviceFailed"));
            return false;
        }
    }, [token, t]);

    const unsubscribe = useCallback(async () => {
        const fcmToken = fcmTokenRef.current;
        if (!fcmToken || !token) {
            setStatus("default");
            return;
        }
        try {
            await unregisterDevice({ fcmToken, token });
        } catch (err) {
            captureError(err, {
                tags: { feature: "fcm", operation: "unregisterDevice" },
            });
        } finally {
            fcmTokenRef.current = null;
            setStatus("default");
        }
    }, [token]);

    return { status, lastError, subscribe, unsubscribe };
}
